import { memo, type HTMLAttributes } from 'react';

import { icons } from './icons';

export type IconName = keyof typeof icons;

export enum IconColor {
  RASPBERRY = 'text-raspberry',
  EGGPLANT = 'text-eggplant',
  EGGPLANT_100 = 'text-eggplant-100',
  EGGPLANT_200 = 'text-eggplant-200',
  EGGPLANT_300 = 'text-eggplant-300',
  EGGPLANT_400 = 'text-eggplant-400',
  EGGPLANT_500 = 'text-eggplant-500',
  WHITE = 'text-white',
  GRAY = 'text-neutral-400',
  TURMERIC = 'text-turmeric',
  BUTTER_600 = 'text-butter-600',
  SOURDOUGH = 'text-sourdough',
  STRAWBERRY_500 = 'text-strawberry-500',
  MINT_500 = 'text-mint-500',
  MINT_300 = 'text-mint-300',
  MINT_200 = 'text-mint-200',
  TABLERUN_RED = 'text-tablerun-red',
  TABLERUN_CREAM = 'text-tablerun-cream',
}

export enum IconFill {
  WHITE = '[--svg-fill:theme(colors.white)]',
  RASPBERRY = '[--svg-fill:theme(colors.raspberry.DEFAULT)]',
  EGGPLANT_100 = '[--svg-fill:theme(colors.eggplant.100)]', // default disabled look
}

export enum IconStroke {
  WHITE = '[--svg-stroke:theme(colors.white)]',
  EGGPLANT = '[--svg-stroke:theme(colors.eggplant.DEFAULT)]',
  EGGPLANT_100 = '[--svg-stroke:theme(colors.eggplant.100)]', // default disabled look
  RASPBERRY = '[--svg-stroke:theme(colors.raspberry.DEFAULT)]',
}

export type IconSize = 8 | 12 | 16 | 20 | 24 | 28 | 32 | 48 | 56 | 64 | 100 | 128;

interface Props extends HTMLAttributes<HTMLDivElement> {
  name: IconName;
  color?: IconColor;
  fillColor?: IconFill;
  strokeColor?: IconStroke;
  size?: IconSize;
  width?: IconSize;
  height?: IconSize;
}

/**
 * @param name string key icon name
 * @param color optional `currentColor` for the icon; both fill and stroke inherit this unless overridden
 * @param fillColor optional override for two-tone icons that read `--svg-fill` (e.g. plus/minus)
 * @param strokeColor optional override for two-tone icons that read `--svg-stroke` (e.g. plus/minus)
 * @param size optional pixel size of the icon
 */
export const Icon: React.FC<Props> = memo(
  ({ name, size = 16, width, height, color, fillColor, strokeColor, className, ...rest }) => {
    const SvgIcon = icons[name];

    if (!SvgIcon) return null;

    return (
      <span
        {...rest}
        className={[color, fillColor, strokeColor, className].filter(Boolean).join(' ')}
        aria-label={name.toString()}
        role='img'>
        <SvgIcon height={height ?? size} width={width ?? size} />
      </span>
    );
  }
);

Icon.displayName = 'Icon';
